/*
  Serviço de impressão silenciosa via QZ Tray, com fallback para window.print.
  - Não abre o diálogo de impressão quando QZ Tray está instalado e conectado.
  - Suporta impressora padrão ou uma específica por nome.
  - Carrega o script do QZ dinamicamente para evitar alterações no index.html.
*/

// Tipagem mínima para o objeto global do QZ
declare global {
  interface Window { qz?: any }
}

const QZ_CDN = "https://cdn.qz.io/qz-tray.js";

function loadQZScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.qz) { resolve(); return; }
    const el = document.createElement('script');
    el.src = QZ_CDN;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('Falha ao carregar qz-tray.js'));
    document.body.appendChild(el);
  });
}

// Configura segurança do QZ. Para ambiente de testes, é possível habilitar "Allow unsigned" no QZ Tray.
function setupQZSecurity() {
  if (!window.qz) return;
  // Caso existam certificados configurados no projeto, estes podem ser injetados aqui.
  // Por padrão manteremos sem assinatura e dependeremos da configuração local do QZ Tray.
  try {
    window.qz.security?.setCertificatePromise?.(() => Promise.reject('Sem certificado configurado'));
    window.qz.security?.setSignaturePromise?.((toSign: string) => Promise.reject('Sem assinatura configurada'));
  } catch {}
}

async function ensureQZConnected(): Promise<void> {
  await loadQZScript();
  const qz = window.qz;
  if (!qz) throw new Error('QZ Tray não disponível');
  setupQZSecurity();
  if (!qz.websocket.isActive()) {
    await qz.websocket.connect();
  }
}

export async function isQZAvailable(): Promise<boolean> {
  try {
    await ensureQZConnected();
    return true;
  } catch {
    return false;
  }
}

export async function getDefaultPrinter(): Promise<string | null> {
  try {
    await ensureQZConnected();
    const name = await window.qz.printers.getDefault();
    return name || null;
  } catch {
    return null;
  }
}

export type SilentPrintOptions = {
  deviceName?: string | null; // nome da impressora; se ausente usa padrão
  title?: string; // título do trabalho de impressão
  html?: string; // HTML a ser impresso; se vazio, usa o conteúdo da página
};

export async function printSilently(options: SilentPrintOptions = {}): Promise<void> {
  const { deviceName, title = 'Cupom', html } = options;
  await ensureQZConnected();
  const qz = window.qz;
  const device = deviceName || await qz.printers.getDefault();
  const cfg = qz.configs.create(device, {
    colorType: 'grayscale',
    copies: 1,
    margins: { top: 0, right: 0, bottom: 0, left: 0 },
    jobName: title,
    scale: 1,
    units: 'mm',
    size: { width: 80, height: null }, // Largura 80mm (POS-80); altura automática
  });

  const htmlContent = html || document.querySelector('.max-w-md')?.outerHTML || document.body.innerHTML;

  const data = [{ type: 'html', format: 'plain', data: htmlContent }];
  await qz.print(cfg, data);
}

export async function trySilentOrPreview(options: SilentPrintOptions = {}): Promise<{ method: 'silent' | 'preview'; printer?: string | null }> {
  const { deviceName } = options;
  const available = await isQZAvailable();
  if (available) {
    const printer = deviceName || await getDefaultPrinter();
    await printSilently({ ...options, deviceName: printer });
    return { method: 'silent', printer };
  }
  window.print();
  return { method: 'preview' };
}

export default {
  isQZAvailable,
  getDefaultPrinter,
  printSilently,
  trySilentOrPreview,
};